import { debounce } from 'lodash';
import { Moment } from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  CustomThemeProvider,
  DateRangePicker,
  InputSearch,
  ModalUI,
  Selector,
  SelectorOptionPropsInterface,
  TaptapList,
  type ThemeType,
  usePortal
} from 'sonata-react-components';

import { getDateSeed } from '@/helpers/common/seed.helper';
import { getUserId } from '@/helpers/user/user.helpers';
import { getPixels } from '@/services/pixel.service';
import { getPixelImplementationOptions, getPixelTypeOptions, pixelToRow } from './helpers';
import { NewPixel } from './NewPixel/NewPixel';
import { PixelCode } from './PixelCode/PixelCode';

import type { Pixel } from 'sonata-static-storage/lib/schemas/pixel/pixel.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

import './myPixels.scss';

type Props = {
  onClose: () => void;
  user: UserInterface;
  userRoles: UserRoleInterface[];
  theme?: ThemeType;
};

type TaptapListItem = {
  id: string;
  title: string;
  type: string;
  campaigns: string[];
  pixel: Pixel;
  creation: string;
  actions: any[];
};

type Filters = {
  date: {
    beginDate?: Moment;
    endDate?: Moment;
  };
  name: Pixel['name'] | null;
  type: Pixel['type'][];
  implementation: Pixel['codeType'][];
};

type State = {
  filters: Filters;
  showNewPixelModal: boolean;
  pixelToEdit: Pixel | null;
  pixelToShow: Pixel | null;
  seed: string;
};

export function MyPixels({ onClose, user, userRoles, theme = 'light' }: Props) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const [{ filters, showNewPixelModal, pixelToEdit, pixelToShow, seed }, setState] = useState<State>({
    filters: {
      date: {},
      implementation: [],
      name: null,
      type: []
    },
    pixelToEdit: null,
    pixelToShow: null,
    seed: getDateSeed(),
    showNewPixelModal: false
  });

  const userId = getUserId(user);
  const BASE_ID = 'myPixels';
  const DATE_FORMAT = 'DD-MM-YYYY';

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  const handleTypeChange = (selection: SelectorOptionPropsInterface[]) => {
    const types = selection.map(s => s.id as Pixel['type']);
    setState(prev => ({ ...prev, filters: { ...prev.filters, type: types }, seed: getDateSeed() }));
  };

  const handleImplementationChange = (selection: SelectorOptionPropsInterface[]) => {
    const implementation = selection.map(s => s.id as Pixel['codeType']);
    setState(prev => ({ ...prev, filters: { ...prev.filters, implementation: implementation } }));
  };

  const handleDatesChange = (dates: { endDate: Moment | null; startDate: Moment | null }) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        date: {
          beginDate: dates.startDate ?? undefined,
          endDate: dates.endDate ?? undefined
        }
      },
      ...(((dates.endDate && dates.startDate) || (!dates.endDate && !dates.startDate)) && { seed: getDateSeed() })
    }));
  };

  const handleSearchChange = debounce((value: string) => {
    setState(prev => ({ ...prev, filters: { ...prev.filters, name: value || null }, seed: getDateSeed() }));
  }, 500);

  const handleShowPixel = (pixel: Pixel | null) => {
    setState(prev => ({
      ...prev,
      pixelToShow: pixel
    }));
  };

  const handleShowNewPixelModal = (isOpen: boolean) => {
    setState(prev => ({
      ...prev,
      pixelToEdit: null,
      showNewPixelModal: isOpen
    }));
  };

  const handleSelectPixelToEdit = (pixel: Pixel | null) => {
    setState(prev => ({
      ...prev,
      pixelToEdit: pixel,
      showNewPixelModal: true
    }));
  };

  const handleSavePixel = () => {
    setState(prev => ({
      ...prev,
      pixelToEdit: null,
      seed: getDateSeed(),
      showNewPixelModal: false
    }));
  };

  const fetchPixels = async ({ page, pageSize }: { page: number; pageSize: number }) => {
    const pixelTypeIds = filters.type?.map(pixelType => pixelType);
    const pixelSubtypeIds = filters?.implementation.map(i => i);
    const payload = {
      date: filters.date,
      itemsPerPage: pageSize.toString(),
      name: filters.name,
      page: page.toString(),
      subtype: pixelSubtypeIds.length > 0 ? pixelSubtypeIds : undefined,
      type: pixelTypeIds.length > 0 ? pixelTypeIds : undefined
    };
    const pixels = await getPixels(payload, userId as string, {});

    return {
      data: pixels.items.map(pixel => pixelToRow(pixel, handleShowPixel, handleSelectPixelToEdit)),
      page: pixels.currentPage ?? page,
      pageSize: pixels.itemsPerPage ?? pageSize,
      total: pixels.totalItems ?? pixels.items.length,
      totalPages: Math.max(1, Math.ceil((pixels.totalItems ?? pixels.items.length) / (pixels.itemsPerPage ?? pageSize)))
    };
  };

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast={theme}>
      <ModalUI
        closeModalCallback={onClose}
        ref={modalRef}
        rightButton={
          <ButtonUI
            className="myPixels_newButton"
            id={`${BASE_ID}_newPixelBtn`}
            onClick={() => handleShowNewPixelModal(true)}
            text={formatMessage({
              defaultMessage: 'New pixel',
              id: 'menu.myPixels.newPixel'
            })}
          />
        }
        title={formatMessage({
          defaultMessage: 'My Pixels',
          id: 'menu.myPixels.title'
        })}
        width="1086px"
      >
        <div className="myPixels">
          <div className="myPixels__filters">
            <Selector
              className="myPixels__filtersPixel"
              id={`${BASE_ID}_pixelTypeSelector`}
              label={formatMessage({
                defaultMessage: 'Pixel Type',
                id: 'menu.myPixels.filter.pixelType'
              })}
              onChange={handleTypeChange}
              options={getPixelTypeOptions(user, userRoles, formatMessage)}
              showSearch={false}
              showSelectAll={false}
              showSelectedItems={false}
              tiny
              type="multiple"
            />
            <Selector
              className="myPixels__filtersImplementation"
              id={`${BASE_ID}_implementationTypeSelector`}
              label={formatMessage({
                defaultMessage: 'Implementation type',
                id: 'menu.myPixels.filter.implementation'
              })}
              onChange={handleImplementationChange}
              options={getPixelImplementationOptions(formatMessage)}
              showSearch={false}
              showSelectAll={false}
              showSelectedItems={false}
              tiny
              type="multiple"
            />
            <DateRangePicker
              displayFormat={DATE_FORMAT}
              endDate={filters.date.endDate ?? null}
              endDateId={`${BASE_ID}_date_range_end_date`}
              endDatePlaceholderText={formatMessage({
                defaultMessage: 'To',
                id: 'menu.myPixels.filter.date.to'
              })}
              focusedInput={null}
              inputIconPosition="after"
              isOutsideRange={() => false}
              label={formatMessage({
                defaultMessage: 'Date filter',
                id: 'menu.myPixels.filter.date.label'
              })}
              minimumNights={0}
              onDatesChange={handleDatesChange}
              onFocusChange={() => null}
              showClearDates
              startDate={filters.date.beginDate ?? null}
              startDateId={`${BASE_ID}_date_range_start_date`}
              startDatePlaceholderText={formatMessage({
                defaultMessage: 'From',
                id: 'menu.myPixels.filter.date.from'
              })}
              tiny
            />
            <div className="search">
              <InputSearch
                className="myPixels__filtersName"
                id={`${BASE_ID}_searchByName`}
                lens
                name="myPixelsName"
                onChange={handleSearchChange}
                placeholder={formatMessage({
                  defaultMessage: 'Search by name...',
                  id: 'menu.myPixels.filter.namePlaceholder'
                })}
                tiny
              />
            </div>
          </div>
          <div className="myPixels__listContainer">
            <TaptapList className="myPixels__list" fetchData={fetchPixels} id={`${BASE_ID}_pixelList`} key={seed}>
              <TaptapList.ColumnsBar>
                <TaptapList.ColumnsBar.Column
                  className="pixelName"
                  description={formatMessage({
                    defaultMessage: 'Pixel name',
                    id: 'menu.myPixels.list.toolbar.pixelName'
                  })}
                  id="pixelName"
                />
                <TaptapList.ColumnsBar.Column
                  className="pixelType"
                  description={formatMessage({
                    defaultMessage: 'Pixel type',
                    id: 'menu.myPixels.list.toolbar.pixelType'
                  })}
                  id="pixelType"
                />
                <TaptapList.ColumnsBar.Column
                  className="pixelCampaigns"
                  description={formatMessage({
                    defaultMessage: 'Campaigns',
                    id: 'menu.myPixels.list.toolbar.pixelCampaigns'
                  })}
                  id="pixelCampaigns"
                />
                <TaptapList.ColumnsBar.Column
                  className="pixelCreation"
                  description={formatMessage({
                    defaultMessage: 'Date of creation',
                    id: 'menu.myPixels.list.toolbar.pixelCreation'
                  })}
                  id="pixelCreation"
                />
              </TaptapList.ColumnsBar>
              <TaptapList.RowsList<TaptapListItem>
                emptyStateDescription={formatMessage({
                  defaultMessage: 'Try adjusting your search or filters to find pixels.',
                  id: 'menu.myPixels.list.emptyState.description'
                })}
                emptyStateIcon="fal fa-empty-set"
                emptyStateTitle={formatMessage({
                  defaultMessage: 'No results match your filters',
                  id: 'menu.myPixels.list.emptyState.title'
                })}
              >
                {itemsList =>
                  itemsList.map(item => (
                    <TaptapList.RowsList.Row id={item.id} key={item.id}>
                      <span className="pixelTitle">{item.title}</span>
                      <span className="pixelType">{item.type}</span>
                      <span className="pixelCampaigns">{item.campaigns.length}</span>
                      <span className="pixelCreation">{item.creation}</span>
                      <TaptapList.RowsList.Row.Actions actions={item.actions} />
                    </TaptapList.RowsList.Row>
                  ))
                }
              </TaptapList.RowsList>
            </TaptapList>
          </div>
        </div>
        {pixelToShow && <PixelCode onClose={() => handleShowPixel(null)} pixel={pixelToShow} />}
        {showNewPixelModal && (
          <NewPixel
            onClose={() => handleShowNewPixelModal(false)}
            onSave={handleSavePixel}
            pixel={pixelToEdit}
            user={user}
            userRoles={userRoles}
          />
        )}
      </ModalUI>
    </CustomThemeProvider>,
    usePortal('myPixelsModal')
  );
}
